import { useCallback } from 'react'
import CRMIntegrationService from '@forwardflow/services/CRMIntegrationServices'
import { isOperatorEmail } from '@forwardflow/utils/app.utils'
import OrganizationService from '@forwardflow/services/OrganizationService'
import { UserService } from '@forwardflow/services/UserService'
import { useAppStore } from '@forwardflow/store'
import { useNavigate } from 'react-router-dom'

interface UseUserNavigationParams {
   user?: {
      email?: string
      userOrganizations?: Array<{ organizationId: string }>
   }
   currentOrgId?: string
   searchParams?: string
   shouldNotNavigateToDashboard?: boolean
}

/**
 * Custom hook to determine and navigate to the appropriate route based on user state.
 * This hook encapsulates the logic for:
 * - Getting the current organization ID based on user type (operator vs regular user)
 * - Checking if user is an operator
 * - Checking organization status
 * - Validating onboarding completion via 24-hour delay
 * - Navigating to the correct destination
 */
export const useUserNavigation = () => {
   const { setCurrentOrganization, currentOrganization } = useAppStore()
   const navigate = useNavigate()
   /**
    * Gets the current organization ID for a user.
    * For operators: checks organizationList from API
    * For regular users: checks user.userOrganizations
    * Prefers stored org ID from localStorage if it exists in the list,
    * otherwise returns the first available org.
    */
   const getCurrentOrgId = useCallback(async (): Promise<
      string | undefined
   > => {
      const userData = await UserService.getUserData()
      const storedOrgId = currentOrganization
      const isOperator = isOperatorEmail(userData?.email || '')

      // Helper to get valid operator org ID
      const getOperatorOrgId = async () => {
         const organizationList = await OrganizationService.getOrganization({
            page: 1,
            limit: 10,
         })
         if (!organizationList?.length) return undefined

         // Return stored org if it exists in the list
         if (
            storedOrgId &&
            organizationList.some((org) => org.id === storedOrgId)
         ) {
            return storedOrgId
         }

         // Fallback to first available org
         return organizationList[0].id
      }

      // Helper to get valid user org ID
      const getUserOrgId = async () => {
         const userOrgs = userData?.userOrganizations || []
         if (!userOrgs?.length) return undefined

         // Return stored org if it exists in user's orgs
         if (
            storedOrgId &&
            userOrgs.some((org) => org.organizationId === storedOrgId)
         ) {
            return storedOrgId
         }

         // Fallback to first available org
         return userOrgs[0].organizationId
      }

      return isOperator ? await getOperatorOrgId() : await getUserOrgId()
   }, [currentOrganization])

   /**
    * Determines the correct navigation path for a user and navigates there.
    * Returns the path it navigated to for use in cases where you need the path.
    */
   const navigateUser = useCallback(
      async ({
         currentOrgId,
         searchParams = '',
         shouldNotNavigateToDashboard,
      }: UseUserNavigationParams): Promise<string> => {
         const search = searchParams || ''

         // 1. If user has no organizations, redirect to onboarding
         if (!currentOrgId) {
            const path = '/onboarding'
            navigate(path)
            return path
         }

         // 2. If user has organizations, check 24-hour delay status
         setCurrentOrganization(currentOrgId)
         const res = await CRMIntegrationService.get24HourDelay(currentOrgId)

         // If onboarding not complete (24-hour delay not disabled), go to onboarding
         if (!res?.isDisabled) {
            const path = '/onboarding'
            navigate(path)
            return path
         }

         if (!shouldNotNavigateToDashboard) {
            const path = `/dashboard${search}`
            window.location.href = path
            return path
         }

         return ''
      },
      [setCurrentOrganization, navigate]
   )

   /**
    * Gets the appropriate path without navigating.
    * Useful for conditional logic or previewing where user would be sent.
    */
   const getUserPath = useCallback(
      async ({
         user,
         currentOrgId,
         searchParams = '',
      }: UseUserNavigationParams): Promise<string> => {
         const search = searchParams || ''

         // Allow operators to navigate to dashboard
         const isOperator = isOperatorEmail(user?.email || '')
         if (isOperator) {
            return `/dashboard${search}`
         }

         // 1. If user has no organizations, redirect to onboarding
         if (!currentOrgId) {
            return '/onboarding'
         }

         // 2. If user has organizations, check 24-hour delay status
         const res = await CRMIntegrationService.get24HourDelay(currentOrgId)

         // If onboarding not complete (24-hour delay not disabled), go to onboarding
         if (!res?.isDisabled) {
            return '/onboarding'
         }

         // Otherwise, go to dashboard (onboarding is complete)
         return `/dashboard${search}`
      },
      []
   )

   return { navigateUser, getUserPath, getCurrentOrgId }
}
